import { useSearchParams } from 'react-router-dom';
import { useMemo, useCallback } from 'react';
import { debounce } from 'lodash';

export interface NotificationApiFilters {
  search?: string;
  filter?: string[];
  filterType?: string[];
  filterModule?: string[];
}

export interface UrlFilterParams {
  type: string[];
  module: string[];
  search: string;
  filter: string[];
}

export type FilterCategory = keyof UrlFilterParams;

export const useNotificationsFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlParams: UrlFilterParams = useMemo(() => ({
    type: searchParams.get('type')?.split(',').filter(Boolean) || [],
    module: searchParams.get('module')?.split(',').filter(Boolean) || [],
    search: searchParams.get('search') || '',
    filter: searchParams.get('filter')?.split(',').filter(Boolean) || [],
  }), [searchParams]);

  const apiFilters: NotificationApiFilters = useMemo(() => {
    const filters: NotificationApiFilters = {};
    if (urlParams.search) filters.search = urlParams.search;
    if (urlParams.filter.length > 0) filters.filter = urlParams.filter;
    if (urlParams.type.length > 0) filters.filterType = urlParams.type;
    if (urlParams.module.length > 0) filters.filterModule = urlParams.module;
    return filters;
  }, [urlParams]);


  const updateUrlParams = useCallback((param: FilterCategory, value: string | string[]) => {
    const newParams = new URLSearchParams(searchParams);
    const valueToSet = Array.isArray(value) ? value.join(',') : value;

    if (!valueToSet) {
      newParams.delete(param);
    } else {
      newParams.set(param, valueToSet);
    }

    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);


  const debouncedUpdateSearch = useMemo(() => debounce((searchValue: string) => {
    updateUrlParams('search', searchValue);
  }, 400), [updateUrlParams]);


  const onSelect = useCallback((param: FilterCategory, value: string, isToggle = false) => {
    if (param === 'search') {
      debouncedUpdateSearch(value);
      return;
    }

    if (!isToggle) {
      updateUrlParams(param, value);
      return;
    }

    if (param === 'type' || param === 'module') {
      const currentValue = urlParams[param][0] || '';
      const newValue = currentValue === value ? '' : value;
      updateUrlParams(param, newValue);
      return;
    }

    if (param === 'filter') {
      const currentValues = urlParams.filter;
      const newValues = currentValues.includes(value)
        ? currentValues.filter(f => f !== value)
        : [...currentValues, value];
      updateUrlParams(param, newValues);
      return;
    }
  }, [urlParams, updateUrlParams, debouncedUpdateSearch]);

  return {
    apiFilters,
    urlParams,
    onSelect,
  };
};